import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Box, Button, TextField, Typography, Paper, InputAdornment, IconButton, Alert, Container, CircularProgress, keyframes } from '@mui/material';
import { LockIcon, EyeIcon, EyeOffIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
// Define keyframes for animations
const glowPulse = keyframes`
  0% { box-shadow: 0 0 5px rgba(0, 180, 216, 0.5); }
  50% { box-shadow: 0 0 20px rgba(0, 180, 216, 0.8); }
  100% { box-shadow: 0 0 5px rgba(0, 180, 216, 0.5); }
`;
const floatingAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;
const rippleAnimation = keyframes`
  0% { transform: scale(0); opacity: 1; }
  100% { transform: scale(2.5); opacity: 0; }
`;
const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
  20%, 40%, 60%, 80% { transform: translateX(5px); }
`;
const ResetPasswordPage: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showCapsLock, setShowCapsLock] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [rippleEffect, setRippleEffect] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const {
    resetPassword
  } = useAuth();
  const navigate = useNavigate();
  const validatePassword = (value: string) => {
    if (!value) return 'Password is required';
    const minLength = value.length >= 8;
    const maxLength = value.length <= 15;
    const hasUppercase = /[A-Z]/.test(value);
    const hasLowercase = /[a-z]/.test(value);
    const hasNumber = /[0-9]/.test(value);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(value);
    if (!minLength || !maxLength) {
      return 'Password must be between 8-15 characters';
    }
    if (!hasUppercase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowercase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!hasNumber) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecial) {
      return 'Password must contain at least one special character';
    }
    return '';
  };
  const validateForm = () => {
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
      return false;
    }
    return true;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setIsLoading(true);
    setRippleEffect(true);
    try {
      const success = await resetPassword(password, confirmPassword);
      if (success) {
        setIsSuccess(true);
        navigate('/login', {
          state: {
            message: 'Password has been reset successfully. You can now log in with your new password.'
          }
        });
      } else {
        setError('Failed to reset password. Please try again.');
        setIsShaking(true);
        setTimeout(() => setIsShaking(false), 500);
      }
    } catch (error) {
      setError('An error occurred. Please try again later.');
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 500);
    } finally {
      setIsLoading(false);
      setRippleEffect(false);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.getModifierState('CapsLock')) {
      setShowCapsLock(true);
    } else {
      setShowCapsLock(false);
    }
  };
  return <Box sx={{
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8F9FA',
    position: 'relative',
    px: 2,
    overflow: 'hidden'
  }} data-id="element-247">
      <Container maxWidth="sm" sx={{
      mb: 4,
      mt: 4,
      position: 'relative',
      zIndex: 1
    }} data-id="element-248">
        <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mb: 3
      }} data-id="element-249">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/xyh4FinweYvNRM6XVnTFSv/openRemitLogo-1.png" alt="OpenRemit Logo" style={{
          height: 80,
          marginBottom: 20
        }} data-id="element-250" />
          <Typography variant="subtitle1" sx={{
          color: '#6B7280',
          textAlign: 'center',
          fontWeight: 600,
          letterSpacing: '1px'
        }} data-id="element-251">
            Account Recovery
          </Typography>
        </Box>
        <Paper elevation={0} sx={{
        p: {
          xs: 3,
          sm: 4
        },
        width: '100%',
        maxWidth: 420,
        mx: 'auto',
        backgroundColor: '#FFFFFF',
        border: '1px solid #E5E7EB',
        borderRadius: '8px',
        boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
        position: 'relative',
        animation: isShaking ? `${shakeAnimation} 0.5s ease-in-out` : 'none'
      }} data-id="element-252">
          {isSuccess ? <Box sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          backgroundColor: '#ECFDF5',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 3
        }} data-id="element-253">
              <CheckCircleIcon size={40} color="#0B7923" data-id="element-254" />
            </Box> : <Typography variant="h5" component="h2" sx={{
          mb: 1,
          textAlign: 'center',
          color: '#1F2937',
          fontWeight: 700
        }} data-id="element-255">
              Create New Password
            </Typography>}
          <Typography variant="body2" color="text.secondary" sx={{
          mb: 3,
          textAlign: 'center'
        }} data-id="element-256">
            Your password must be at least 8 characters and include an uppercase
            letter, lowercase letter, number, and special character.
          </Typography>
          {error && <Alert severity="error" sx={{
          mb: 3,
          borderRadius: '12px',
          border: '1px solid rgba(255, 93, 143, 0.3)',
          animation: isShaking ? `${shakeAnimation} 0.5s ease-in-out` : 'none'
        }} data-id="element-257">
              {error}
            </Alert>}
          <form onSubmit={handleSubmit} data-id="element-258">
            <TextField id="password" label="New Password" type={showPassword ? 'text' : 'password'} variant="outlined" fullWidth value={password} onChange={e => setPassword(e.target.value)} onKeyDown={handleKeyDown} error={!!error && error.includes('Password')} helperText={showCapsLock ? 'Caps lock is on' : ''} InputProps={{
            startAdornment: <InputAdornment position="start" data-id="element-260">
                    <LockIcon size={20} color="#0052CC" data-id="element-261" />
                  </InputAdornment>,
            endAdornment: <InputAdornment position="end" data-id="element-262">
                    <IconButton aria-label="toggle password visibility" onClick={() => setShowPassword(!showPassword)} edge="end" sx={{
                color: '#0052CC',
                '&:hover': {
                  background: 'rgba(0, 85, 139, 0.1)'
                }
              }} data-id="element-263">
                      {showPassword ? <EyeOffIcon size={20} data-id="element-264" /> : <EyeIcon size={20} data-id="element-265" />}
                    </IconButton>
                  </InputAdornment>
          }} sx={{
            mb: 2.5,
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused': {
                boxShadow: '0 0 0 4px rgba(0, 85, 139, 0.15)'
              }
            }
          }} data-id="element-259" />
            <TextField id="confirmPassword" label="Confirm New Password" type={showConfirmPassword ? 'text' : 'password'} variant="outlined" fullWidth value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} onKeyDown={handleKeyDown} error={!!error && error.includes('match')} helperText={showCapsLock ? 'Caps lock is on' : ''} InputProps={{
            startAdornment: <InputAdornment position="start" data-id="element-267">
                    <LockIcon size={20} color="#0052CC" data-id="element-268" />
                  </InputAdornment>,
            endAdornment: <InputAdornment position="end" data-id="element-269">
                    <IconButton aria-label="toggle confirm password visibility" onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" sx={{
                color: '#0052CC',
                '&:hover': {
                  background: 'rgba(0, 85, 139, 0.1)'
                }
              }} data-id="element-270">
                      {showConfirmPassword ? <EyeOffIcon size={20} data-id="element-271" /> : <EyeIcon size={20} data-id="element-272" />}
                    </IconButton>
                  </InputAdornment>
          }} sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused': {
                boxShadow: '0 0 0 4px rgba(0, 85, 139, 0.15)'
              }
            }
          }} data-id="element-266" />
            <Box sx={{
            position: 'relative',
            overflow: 'hidden'
          }} data-id="element-273">
              <Button type="submit" variant="contained" color="primary" fullWidth disabled={isLoading || !password || !confirmPassword} sx={{
              py: 1.5,
              mb: 2,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #0077B6 0%, #00B4D8 100%)',
              boxShadow: '0 4px 14px rgba(0, 119, 182, 0.3)',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(0, 119, 182, 0.4)',
                background: 'linear-gradient(135deg, #0077B6 0%, #48CAE4 100%)',
                '&::after': {
                  opacity: 1
                }
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, #0077B6 20%, #00B4D8 50%, #0077B6 80%)',
                backgroundSize: '200% 200%',
                opacity: 0,
                transition: 'opacity 0.3s ease',
                zIndex: 0,
                animation: `${gradientShift} 3s linear infinite`
              },
              '& .MuiButton-label': {
                position: 'relative',
                zIndex: 1
              },
              '&:disabled': {
                background: '#e0e0e0',
                color: '#9e9e9e'
              },
              '&.Mui-disabled': {
                background: 'rgba(0, 119, 182, 0.2)'
              }
            }} data-id="element-274">
                {isLoading ? <CircularProgress size={24} color="inherit" sx={{
                color: 'white',
                animation: `${glowPulse} 1.5s infinite ease-in-out`
              }} data-id="element-275" /> : 'Reset Password'}
              </Button>
              {rippleEffect && <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '100%',
              height: '100%',
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.5)',
                animation: `${rippleAnimation} 1s forwards`
              }
            }} data-id="element-276" />}
            </Box>
          </form>
          <Button onClick={() => navigate('/login')} variant="contained" fullWidth sx={{
          py: 1.5,
          borderRadius: '6px',
          backgroundColor: '#0052CC',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: '#003DA8',
            boxShadow: '0px 1px 3px rgba(0,0,0,0.12)'
          }
        }} data-id="element-277">
            Return to Login
          </Button>
        </Paper>
      </Container>
      <Box component="footer" sx={{
      py: 3,
      width: '100%',
      textAlign: 'center',
      mt: 'auto',
      position: 'relative',
      zIndex: 1
    }} data-id="element-278">
        <Typography variant="body2" sx={{
        background: 'linear-gradient(90deg, #00B4D8, #0077B6)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        fontWeight: 500,
        textShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
      }} data-id="element-279">
          Powered by Paysys
        </Typography>
      </Box>
    </Box>;
};
export default ResetPasswordPage;